import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const emptyForm = { email: "", name: "", phone: "", password: "", role: "employee", company_id: "", store_id: "" };

export default function Users() {
  const { companies, stores, user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const isRegional = me?.role === "regional_manager";
  const isMgrLike = me?.role === "manager" || me?.role === "hr";

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, company_id: companies[0]?.id || "" });
    setOpen(true);
  };
  const startEdit = (r) => {
    setEditing(r);
    setForm({ email: r.email, name: r.name, phone: r.phone || "", password: "", role: r.role, company_id: r.company_id || "", store_id: r.store_id || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Client-side validation — backend also rejects, but toast faster locally
    if ((form.role === "employee" || isRegional) && !form.store_id) {
      toast.error(`${form.role.replace("_", " ")} accounts must be assigned to a store`);
      return;
    }
    try {
      const payload = { ...form };
      if (editing) {
        const patch = { name: form.name, phone: form.phone, role: form.role, company_id: form.company_id, store_id: form.store_id };
        if (form.password) patch.password = form.password;
        await api.patch(`/users/${editing.id}`, patch);
        toast.success("User updated");
      } else {
        await api.post("/users", payload);
        toast.success("User created");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";
  const storeName = (id) => {
    const s = stores?.find((x) => x.id === id);
    return s ? `${s.city} — ${s.name}` : "—";
  };
  const requiresStore = form.role === "employee" || isRegional;
  const regionStores = isRegional
    ? (stores || []).filter((s) => (me?.store_ids || []).includes(s.id))
    : (stores || []);
  const canManageRow = (r) => me?.role === "admin"
    || (isMgrLike && r.role === "employee" && r.company_id === me.company_id)
    || (isRegional && r.id !== me.id && ["manager", "employee"].includes(r.role) && (me?.store_ids || []).includes(r.store_id));

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Administration</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Users & Roles</h1>
        </div>
        {me?.role === "admin" || isMgrLike || isRegional ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startAdd} className="h-9" data-testid="user-add-btn">
                <Plus size={15} weight="bold" className="mr-1.5" /> {isMgrLike ? "New Employee" : "New User"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight">
                  {editing ? "Edit User" : (isMgrLike ? "New Employee" : "New User")}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="eyebrow">Name *</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="eyebrow">Email ID (Login ID) *</Label>
                    <Input required type="email" disabled={!!editing} value={form.email}
                      placeholder="user@example.com"
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="font-mono" data-testid="user-email" />
                    <div className="text-[11px] text-muted-foreground">This email is used as the login ID.</div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="eyebrow">Mobile Number</Label>
                    <Input type="tel" value={form.phone}
                      placeholder="+91 98765 43210"
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="font-mono" data-testid="user-phone" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">{editing ? "New Password (optional)" : "Password *"}</Label>
                  <Input required={!editing} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="eyebrow">Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={isMgrLike}>
                      <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {me?.role === "admin" && <SelectItem value="admin">Admin</SelectItem>}
                        {(me?.role === "admin" || isRegional) && <SelectItem value="manager">Manager</SelectItem>}
                        {me?.role === "admin" && <SelectItem value="regional_manager">Regional Manager</SelectItem>}
                        {me?.role === "admin" && <SelectItem value="hr">HR</SelectItem>}
                        {/* store_manager role deprecated — a manager assigned to a store is locked to it, same behaviour */}
                        <SelectItem value="employee">Employee</SelectItem>
                      </SelectContent>
                    </Select>
                    {isMgrLike && (
                      <div className="text-[11px] text-muted-foreground">{me?.role === "hr" ? "HR" : "Managers"} can only create Employee accounts.</div>
                    )}
                    {isRegional && (
                      <div className="text-[11px] text-muted-foreground">You can create Managers and Employees for your stores.</div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="eyebrow">Company</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })} disabled={isMgrLike || isRegional}>
                      <SelectTrigger data-testid="user-company"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">
                    Assigned Store {requiresStore && <span className="text-destructive">*</span>}
                  </Label>
                  <Select value={form.store_id || "__none__"} onValueChange={(v) => setForm({ ...form, store_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="user-store"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      {!isRegional && <SelectItem value="__none__">Unassigned (admin/manager only)</SelectItem>}
                      {regionStores.filter((s) => s.active !== false).map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.city} — {s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-muted-foreground">
                    {form.role === "employee"
                      ? "Employees are locked to a single store's data."
                      : form.role === "manager"
                      ? "Managers pinned to a store are locked to it; leave blank to see all stores (HQ)."
                      : form.role === "regional_manager"
                      ? "Regional Manager scope is set via the multi-store picker below — this single field is ignored."
                      : "Admins and Managers can leave this blank to see all stores."}
                  </div>
                </div>
                {form.role === "regional_manager" && (
                  <div className="space-y-1.5" data-testid="user-regional-stores">
                    <Label className="eyebrow">
                      Stores in Region <span className="text-destructive">*</span>
                    </Label>
                    <div className="border border-border rounded-md p-2 max-h-56 overflow-y-auto space-y-1">
                      {(stores || []).filter((s) => s.active !== false).length === 0 && (
                        <div className="text-[11.5px] text-muted-foreground italic p-2">
                          No stores yet — create stores first from the Stores tab.
                        </div>
                      )}
                      {(stores || []).filter((s) => s.active !== false).map((s) => {
                        const checked = (form.store_ids || []).includes(s.id);
                        return (
                          <label key={s.id}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-muted/50 ${checked ? "bg-primary/5" : ""}`}
                            data-testid={`user-region-store-${s.id}`}>
                            <input type="checkbox" checked={checked}
                              onChange={(ev) => {
                                const cur = form.store_ids || [];
                                setForm({
                                  ...form,
                                  store_ids: ev.target.checked
                                    ? [...cur, s.id]
                                    : cur.filter((x) => x !== s.id),
                                });
                              }} />
                            <span className="text-[13px]">
                              <b>{s.city}</b> — {s.name}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Tick every store this Regional Manager should oversee. They'll see rolled-up
                      data across all ticked stores and can drill down into any one of them.
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="user-save">{editing ? "Update" : "Create"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        ) : null}
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email (Login ID)</th>
              <th>Mobile</th>
              <th>Role</th>
              <th>Company</th>
              <th>Store</th>
              <th>Status</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`user-row-${r.id}`}>
                <td className="font-medium">{r.name}</td>
                <td className="font-mono text-[12.5px]">{r.email}</td>
                <td className="font-mono text-[12.5px]">{r.phone || "—"}</td>
                <td><span className={`pill ${r.role === "admin" ? "pill-info" : r.role === "manager" ? "pill-warning" : "pill-muted"}`}>{r.role.replace("_", " ")}</span></td>
                <td>{companyName(r.company_id)}</td>
                <td className="text-[12px]">{storeName(r.store_id)}</td>
                <td>{r.active ? <span className="pill pill-success">Active</span> : <span className="pill pill-danger">Disabled</span>}</td>
                <td className="num" style={{ textAlign: "right" }}>
                  {canManageRow(r) && (
                    <>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(r)} data-testid={`user-edit-${r.id}`}><PencilSimple size={14} /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)} data-testid={`user-delete-${r.id}`}><Trash size={14} /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
